import { useState, useEffect, useCallback, useRef } from "react";
import { toast } from "react-toastify";
import { X, Loader2, Edit, Upload } from "lucide-react";
import { useSuperAdmin } from "../../context/SuperAdminContext";

const COLOR = "#3B82F6";

// Keep field names consistent everywhere (id / name / logo / status / price / isReturnable)
const normalizeAccessories = (accessory) => ({
  id: accessory._id || accessory.id,
  name: accessory.accessoryName,
  logo: accessory.logo || "",
  status: accessory.isActive !== undefined ? accessory.isActive : true,
  price: accessory.price !== undefined ? accessory.price : accessory.itemPrice,
  isReturnable: accessory.isReturnable !== undefined ? accessory.isReturnable : true,
});

// ── Toggle: true = fetch from API, false = use dummy data
const USE_API = true;

// ── Dummy data used when USE_API is false ───────────────────────────────────
const DUMMY_ACCESSORIES = [
  { id: "1", name: "Apron", logo: "https://th.bing.com/th/id/OIP.dEmu_4xc6LYsefN5vb7RIAHaHa?w=180&h=180&c=7&r=0&o=7&pid=1.7&rm=3", status: true, price: 199, isReturnable: true },
  { id: "2", name: "Gloves", logo: "https://th.bing.com/th/id/OIP.JekOJ_AURyiYeA7lM8iLBgHaEK?w=316&h=180&c=7&r=0&o=7&pid=1.7&rm=3", status: true, price: 49, isReturnable: false },
  { id: "3", name: "Helmet", logo: "https://static.vecteezy.com/system/resources/previews/033/047/192/non_2x/orange-safety-helmet-for-a-factory-worker-in-industrial-production-free-photo.jpg", status: false, price: 349, isReturnable: true },
];

const EMPTY_FORM = {
  accessoryName: "",
  isActive: true,
  itemPrice: "",
  isReturnable: true,
  logo: null,
  logoPreview: "",
};

// ── Accessory Modal (Add / Edit) 
function AccessoryModal({ mode, initial, onClose, onSave }) {
  const { add_accessory, edit_accessory } = useSuperAdmin();

  const [form, setForm] = useState(
    initial
      ? {
          accessoryName: initial.name || "",
          isActive: initial.isActive !== undefined ? initial.isActive : true,
          itemPrice: initial.defaultPrice !== undefined ? initial.defaultPrice : "",
          isReturnable: initial.isReturnable !== undefined ? initial.isReturnable : true,
          logo: null, // no new file picked yet — keep existing logo unless user changes it
          logoPreview: initial.logo || "",
        }
      : { ...EMPTY_FORM }
  );
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const fileRef = useRef();

  const set = (key, val) => {
    setForm((p) => ({ ...p, [key]: val }));
    setErrors((p) => ({ ...p, [key]: "" }));
  };

  const handleLogo = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!["image/jpeg", "image/png"].includes(file.type)) {
      setErrors((p) => ({ ...p, accessoryLogo: "Only JPG or PNG files are allowed" }));
      e.target.value = "";
      return;
    }
    const preview = URL.createObjectURL(file);
    setForm((p) => ({ ...p, logo: file, logoPreview: preview }));
    setErrors((p) => ({ ...p, accessoryLogo: "" }));
  };

  const validate = () => {
    const e = {};
    if (!String(form.accessoryName || "").trim()) e.accessoryName = "Essentials name is required";
    if (!form.logoPreview && !form.logo) e.accessoryLogo = "Essentials logo is required";

    const priceStr = String(form.itemPrice ?? "").trim();
    if (!priceStr) {
      e.itemPrice = "Item price is required";
    } else if (isNaN(Number(priceStr)) || Number(priceStr) < 0) {
      e.itemPrice = "Enter a valid price";
    }

    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setSubmitting(true);
    try {
      if (!USE_API) {
        toast.success(mode === "add" ? "Essentials added successfully" : "Essentials updated successfully");
        onSave?.({
          id: initial?.id || String(Date.now()),
          name: form.accessoryName,
          logo: form.logoPreview,
          status: form.isActive,
          price: Number(form.itemPrice),
          isReturnable: form.isReturnable,
        });
        onClose();
        return;
      }

      const fd = new FormData();
      fd.append("name", form.accessoryName ?? "");
      fd.append("price", form.itemPrice ?? "");
      fd.append("status", form.isActive ? "active" : "inactive");
      fd.append("isReturnable", form.isReturnable ? "true" : "false");

      if (form.logo instanceof File) {
        fd.append("logo", form.logo);
      }

      const res =
        mode === "add"
          ? await add_accessory(fd)
          : await edit_accessory(fd, initial._id);

      if (res?.data?.success) {
        toast.success(mode === "add" ? "Essentials added successfully" : "Essentials updated successfully");
        onSave?.(normalizeAccessories(res?.data?.data || {}));
        onClose();
      } else {
        toast.error(res?.data?.message || "Failed to save");
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to save");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[1000]">
      <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4 shadow-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-lg font-bold text-gray-900">
            {mode === "add" ? "Add Essentials" : "Edit Essentials"}
          </h3>
          <button onClick={onClose} disabled={submitting} className="p-1.5 rounded-md hover:bg-gray-100">
            <X size={18} className="text-gray-500" />
          </button>
        </div>

        <div className="space-y-4">
          {/* Accessory Logo */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Essentials Logo <span className="text-red-500">*</span>
            </label>
            <div className="flex items-center gap-4">
              <div
                className={`w-16 h-16 rounded-xl border-2 border-dashed flex items-center justify-center overflow-hidden cursor-pointer transition-all ${
                  errors.accessoryLogo ? "border-red-400" : "border-gray-200 hover:border-blue-400"
                }`}
                onClick={() => fileRef.current.click()}
              >
                {form.logoPreview ? (
                  <img src={form.logoPreview} alt="accessory" className="w-full h-full object-cover" />
                ) : (
                  <Upload size={20} color={errors.accessoryLogo ? "#F87171" : "#D1D5DB"} />
                )}
              </div>
              <div>
                <button
                  type="button"
                  onClick={() => fileRef.current.click()}
                  className="text-xs font-semibold px-3 py-1.5 rounded-lg border border-gray-200 hover:bg-gray-50 transition-all text-gray-600"
                >
                  {form.logoPreview ? "Change Logo" : "Upload Logo"}
                </button>
                <p className="text-xs text-gray-400 mt-1">JPG or PNG only</p>
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/jpeg,image/png"
                  className="hidden"
                  onChange={handleLogo}
                />
              </div>
            </div>
            {errors.accessoryLogo && <p className="text-red-500 text-xs mt-1">{errors.accessoryLogo}</p>}
          </div>

          {/* Accessory Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Essentials Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={form.accessoryName}
              onChange={(e) => set("accessoryName", e.target.value)}
              placeholder="e.g. Apron"
              className={`w-full h-7 px-3 py-2 text-sm border rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 ${
                errors.accessoryName ? "border-red-400 bg-red-50" : "border-gray-300"
              }`}
            />
            {errors.accessoryName && <p className="text-red-500 text-xs mt-1">{errors.accessoryName}</p>}
          </div>

          {/* Item Price */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Essentials Price <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              min="0"
              step="0.01"
              value={form.itemPrice}
              onChange={(e) => set("itemPrice", e.target.value)}
              placeholder="e.g. 100"
              className={`w-full h-7 px-3 py-2 text-sm border rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 ${
                errors.itemPrice ? "border-red-400 bg-red-50" : "border-gray-300"
              }`}
            />
            {errors.itemPrice && <p className="text-red-500 text-xs mt-1">{errors.itemPrice}</p>}
          </div>

          {/* Is Returnable */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Returnable</label>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => set("isReturnable", true)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold border transition-all h-7 ${
                  form.isReturnable
                    ? "bg-green-100 border-green-400 text-green-700"
                    : "bg-white border-gray-300 text-gray-400 hover:border-green-300"
                }`}
              >
                <span className={`w-1.5 h-1.5 rounded-full ${form.isReturnable ? "bg-green-600" : "bg-gray-300"}`} />
                Yes
              </button>
              <button
                type="button"
                onClick={() => set("isReturnable", false)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold border transition-all h-7 ${
                  !form.isReturnable
                    ? "bg-red-100 border-red-400 text-red-700"
                    : "bg-white border-gray-300 text-gray-400 hover:border-red-300"
                }`}
              >
                <span className={`w-1.5 h-1.5 rounded-full ${!form.isReturnable ? "bg-red-600" : "bg-gray-300"}`} />
                No
              </button>
            </div>
          </div>

          {/* Status */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => set("isActive", true)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold border transition-all h-7 ${
                  form.isActive
                    ? "bg-green-100 border-green-400 text-green-700"
                    : "bg-white border-gray-300 text-gray-400 hover:border-green-300"
                }`}
              >
                <span className={`w-1.5 h-1.5 rounded-full ${form.isActive ? "bg-green-600" : "bg-gray-300"}`} />
                Active
              </button>
              <button
                type="button"
                onClick={() => set("isActive", false)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold border transition-all h-7 ${
                  !form.isActive
                    ? "bg-red-100 border-red-400 text-red-700"
                    : "bg-white border-gray-300 text-gray-400 hover:border-red-300"
                }`}
              >
                <span className={`w-1.5 h-1.5 rounded-full ${!form.isActive ? "bg-red-600" : "bg-gray-300"}`} />
                Inactive
              </button>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-100">
          <button
            onClick={handleSubmit}
            disabled={submitting}
            className="px-4 py-2 text-white text-sm font-semibold rounded-lg hover:opacity-90 bg-blue-500 disabled:opacity-60 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {submitting && <Loader2 size={14} className="animate-spin" />}
            {mode === "add" ? "Add Essentials" : "Save Changes"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main Component ───────────────────────────────────────────────────────────
export default function ManageAccessories() {
  const { fetchaccessories } = useSuperAdmin();

  const [accessories, setAccessories] = useState([]);
  const [loading, setLoading] = useState(true);

  const [showAddModal, setShowAddModal] = useState(false);
  const [editAccessory, setEditAccessory] = useState(null);

  const loadAccessories = useCallback(async () => {
    try {
      setLoading(true);

      if (!USE_API) {
        setAccessories(DUMMY_ACCESSORIES);
        return;
      }

      const res = await fetchaccessories();
      if (res?.data?.success && Array.isArray(res?.data?.accessories)) {
        setAccessories(res.data.accessories);
      } else {
        toast.error(res?.data?.message || "Failed to load accessories");
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to load accessories");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAccessories();
  }, []);

  return (
    <div className="p-6" style={{ fontFamily: "'Segoe UI', sans-serif" }}>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg font-bold text-gray-900">Manage Essentials</h2>
          <p className="text-s text-gray-400">
            {loading ? "Loading..." : `Total Essentials : ${accessories.length}`}
          </p>
        </div>

        <button
          onClick={() => setShowAddModal(true)}
          className="px-4 py-1.5 bg-green-600 text-white text-sm rounded-md hover:bg-green-700 flex items-center justify-center gap-1"
        >
          Add Essentials
        </button>
      </div>

      <div className="w-full">
        <div className="max-h-[70vh] overflow-y-auto overflow-x-auto relative border border-gray-100 rounded-lg">
          <table className="w-full text-sm border-collapse table-fixed">
            <colgroup>
              <col style={{ width: "60%" }} />
              <col style={{ width: "10%" }} />
              <col style={{ width: "10%" }} />
              <col style={{ width: "10%" }} />
              <col style={{ width: "10%" }} />
            </colgroup>
            <thead className="sticky top-0 bg-gray-100 shadow-sm z-[50]">
              <tr>
                <th className="p-3 text-left font-bold text-gray-900">Essentials</th>
                <th className="p-3 font-bold text-gray-900">
                  <div className="w-full flex justify-center">Price (₹)</div>
                </th>
                <th className="p-3 font-bold text-gray-900">
                  <div className="w-full flex justify-center">Returnable</div>
                </th>
                <th className="p-3 font-bold text-gray-900">
                  <div className="w-full flex justify-center">Status</div>
                </th>
                <th className="p-3 font-bold text-gray-900">
                  <div className="w-full flex justify-center">Edit</div>
                </th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={5} className="py-16 text-center">
                    <Loader2 size={28} className="animate-spin mx-auto mb-3 text-gray-400" />
                    <p className="text-sm text-gray-400">Loading Essentials...</p>
                  </td>
                </tr>
              ) : accessories.length === 0 ? (
                <tr>
                  <td colSpan={5} className="py-16 text-center">
                    <p className="text-sm text-gray-500 font-medium">No Essentials found.</p>
                  </td>
                </tr>
              ) : (
                accessories.map((row) => (
                  <tr key={row._id || row.id} className="border-b border-gray-100 hover:bg-gray-50 transition-all">
                    {/* Accessory: avatar + name, left aligned */}
                    <td className="p-3 text-left">
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="w-9 h-9 rounded-full border border-gray-200 overflow-hidden bg-gray-50 flex items-center justify-center flex-shrink-0">
                          {row.logo ? (
                            <img src={row.logo} alt={row.name} className="w-full h-full object-cover" />
                          ) : (
                            <Upload size={14} className="text-gray-300" />
                          )}
                        </div>
                        <p
                          className={`text-sm font-semibold truncate ${
                            row.isActive ? "text-gray-700 font-bold" : "text-gray-400"
                          }`}
                        >
                          {row.name}
                        </p>
                      </div>
                    </td>

                    {/* Price */}
                    <td className="p-3">
                      <div className="w-full flex justify-center">
                        <span className="text-sm font-semibold text-gray-700 whitespace-nowrap">
                          {row.defaultPrice !== undefined && row.defaultPrice !== null && row.defaultPrice !== ""
                            ? `${Number(row.defaultPrice)}`
                            : "—"}
                        </span>
                      </div>
                    </td>

                    {/* Returnable */}
                    <td className="p-3">
                      <div className="w-full flex justify-center">
                        <span
                          className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold whitespace-nowrap"
                          style={{
                            background: row.isReturnable ? "#DCFCE7" : "#FEE2E2",
                            color: row.isReturnable ? "#16A34A" : "#DC2626",
                          }}
                        >
                          <span
                            className="w-1.5 h-1.5 rounded-full"
                            style={{ background: row.isReturnable ? "#16A34A" : "#DC2626" }}
                          />
                          {row.isReturnable ? "Yes" : "No"}
                        </span>
                      </div>
                    </td>

                    {/* Status */}
                    <td className="p-3">
                      <div className="w-full flex justify-center">
                        <span
                          className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold whitespace-nowrap"
                          style={{
                            background: row.isActive ? "#DCFCE7" : "#FEE2E2",
                            color: row.isActive ? "#16A34A" : "#DC2626",
                          }}
                        >
                          <span
                            className="w-1.5 h-1.5 rounded-full"
                            style={{ background: row.isActive ? "#16A34A" : "#DC2626" }}
                          />
                          {row.isActive ? "Active" : "Inactive"}
                        </span>
                      </div>
                    </td>

                    {/* Edit */}
                    <td className="p-3">
                      <div className="w-full flex justify-center">
                        <button
                          onClick={() => setEditAccessory(row)}
                          className="p-1.5 rounded-lg hover:bg-blue-50 transition-all"
                          title="Edit Essentials"
                        >
                          <Edit size={14} color={COLOR} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modals */}
      {showAddModal && (
        <AccessoryModal mode="add" onClose={() => setShowAddModal(false)} onSave={() => loadAccessories()} />
      )}
      {editAccessory && (
        <AccessoryModal
          mode="edit"
          initial={editAccessory}
          onClose={() => setEditAccessory(null)}
          onSave={() => loadAccessories()}
        />
      )}
    </div>
  );
}